import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

import './Cart.css';

const API_BASE = 'http://localhost:5001';

function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [success, setSuccess] = useState('');
  const [total, setTotal] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  const cartIsEmpty = !loading && !error && cartItems.length === 0;

  const fetchCart = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setShowConfirmation(false);
    setConfirmationMessage('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setCartItems([]);
        setOrderId(null);
        setTotal(0);
        setError('Veuillez vous connecter pour consulter votre panier.');
        return;
      }

      const res = await axios.get(`${API_BASE}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const userOrders = Array.isArray(res.data)
        ? res.data.filter((order) => order.status === 'pending')
        : [];

      if (userOrders.length > 0) {
        const currentOrder = userOrders[0];
        setCartItems(currentOrder.products || []);
        setOrderId(currentOrder._id);
        setTotal(Number(currentOrder.total) || 0);
      } else {
        setCartItems([]);
        setOrderId(null);
        setTotal(0);
      }
    } catch (err) {
      setError('Erreur lors du chargement du panier');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const handleValidateOrder = async () => {
    if (!orderId) return;
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/api/orders/${orderId}`, {
        status: 'validated'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const confirmationText = 'Commande validée ! Un email contenant le récapitulatif vient de vous être envoyé.';
      setSuccess(confirmationText);
      setConfirmationMessage(confirmationText);
      setShowConfirmation(true);
      setCartItems([]);
      setOrderId(null);
      setTotal(0);
    } catch (err) {
      setError('Erreur lors de la validation de la commande');
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setConfirmationMessage('');
  };

  const handleChangeQuantity = async (productId, delta) => {
    if (!orderId || !productId || !delta) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_BASE}/api/orders/${orderId}`, {
        action: 'updateQuantity',
        productId,
        delta
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedOrder = res.data || {};
      setCartItems(updatedOrder.products || []);
      setOrderId(updatedOrder.products?.length ? updatedOrder._id : null);
      setTotal(Number(updatedOrder.total) || 0);
    } catch (err) {
      const message = err.response?.data?.error || 'Impossible de mettre à jour la quantité.';
      setError(message);
    }
  };

  const handleRemove = async (productId) => {
    if (!orderId) return;
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_BASE}/api/orders/${orderId}`, {
        action: 'remove',
        productId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedOrder = res.data || {};
      setCartItems(updatedOrder.products || []);
      setOrderId(updatedOrder.products?.length ? updatedOrder._id : null);
      setTotal(Number(updatedOrder.total) || 0);
      setSuccess('Article retiré du panier.');
    } catch (err) {
      setError('Erreur lors de la suppression de l’article');
    }
  };

  return (
    <div className="cart-page">
      <div className="cart-card">
        <header className="cart-card__header">
          <h2 className="cart-card__title">Mon panier</h2>
          {!loading && orderId && <span className="cart-card__status">Commande en attente</span>}
        </header>

        {!loading && success && (
          <p className="cart-card__message cart-card__message--success">{success}</p>
        )}

        {loading ? (
          <p className="cart-card__message cart-card__message--info">Chargement...</p>
        ) : error ? (
          <div className="cart-card__empty cart-card__empty--error">
            <p className="cart-card__empty-text">{error}</p>
            <button type="button" className="cart-card__refresh" onClick={fetchCart}>Réessayer</button>
          </div>
        ) : cartIsEmpty ? (
          <div className="cart-card__empty">
            <p className="cart-card__empty-text">Votre panier est actuellement vide.
              <br />Découvrez notre sélection pour remplir votre cave !
            </p>
            <Link to="/products" className="cart-card__cta cart-card__cta--ghost">Explorer les produits</Link>
          </div>
        ) : (
          <>
            <ul className="cart-list">
              {cartItems.map((item, idx) => {
                const productRef = item.product?._id || item.product;
                const productName = item.product?.name || 'Produit';
                const unitPriceRaw = item.product?.price ?? item.price ?? null;
                const unitPrice = typeof unitPriceRaw === 'number' ? unitPriceRaw : Number(unitPriceRaw);

                return (
                  <li key={idx} className="cart-item">
                    <div className="cart-item__info">
                      <span className="cart-item__name">{productName}</span>
                      <span className="cart-item__meta">Quantité : {item.quantity}</span>
                      {Number.isFinite(unitPrice) && unitPrice > 0 && (
                        <span className="cart-item__meta">Prix unitaire : {unitPrice.toFixed(2)} €</span>
                      )}
                    </div>
                    <div className="cart-item__actions">
                      <div className="cart-item__quantity-control">
                        <button
                          type="button"
                          className="cart-item__qty-btn"
                          onClick={() => handleChangeQuantity(productRef, -1)}
                          aria-label={`Diminuer la quantité de ${productName}`}
                        >
                          -
                        </button>
                        <span className="cart-item__qty-value">{item.quantity}</span>
                        <button
                          type="button"
                          className="cart-item__qty-btn"
                          onClick={() => handleChangeQuantity(productRef, 1)}
                          aria-label={`Augmenter la quantité de ${productName}`}
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        className="cart-item__remove"
                        onClick={() => handleRemove(productRef)}
                      >
                        Retirer
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
            <footer className="cart-card__footer">
              <span className="cart-card__total">Total : {total.toFixed(2)} €</span>
              <div className="cart-card__actions">
                <button type="button" className="cart-card__refresh" onClick={fetchCart}>Actualiser</button>
                <button type="button" className="cart-card__cta" onClick={handleValidateOrder}>Valider la commande</button>
              </div>
            </footer>
          </>
        )}
      </div>
      {showConfirmation && (
        <div className="cart-confirmation">
          <div className="cart-confirmation__dialog">
            <h3 className="cart-confirmation__title">Commande validée</h3>
            <p className="cart-confirmation__message">{confirmationMessage}</p>
            <button type="button" className="cart-confirmation__button" onClick={handleCloseConfirmation}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cart;
